import { Injectable, signal, inject } from '@angular/core';
import { Consent } from './consent.model';
import { LanguageService } from '../../language';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, catchError, filter, finalize, map, of, throwError } from 'rxjs';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { ErrorService } from '../../error';
import { AuthService } from '../../auth';
import { LoadingObject, LoadingService } from '../../loaders';

type GenerateConsentsResponse = {
  created: Consent[];
  skipped: Array<{
    slug: string;
    status: number;
    error: string;
    reason_code: string;
  }>;
};

@Injectable({
  providedIn: 'root'
})
export class ConsentService {

  private _consents = signal<Consent[]|undefined>(undefined);
  consents = this._consents.asReadonly();
  private _missingConsentSlugs = new BehaviorSubject<string[]>([]);
  public missingConsentSlugs: Observable<string[]>;
  private _loadingObject = signal<LoadingObject>({});
  private _consentGenerationError = signal<string | undefined>(undefined);
  loadingObject = this._loadingObject.asReadonly();
  consentGenerationError = this._consentGenerationError.asReadonly();
  private loadingService = inject(LoadingService);
  activeLanguageSubscription: Subscription;
  activityTimeStampAttributesUser = signal<string[]>(['given_at', 'declined_at', 'withdrawn_at']).asReadonly();
  activityTimeStampAttributes = signal<string[]>(this.activityTimeStampAttributesUser().concat(['created_at'])).asReadonly();
  currentRoute = signal<ActivatedRoute | undefined>(undefined);
  private consentGenerationAttemptKey: string | undefined;

  constructor(
    private languageService: LanguageService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService,
    private authService: AuthService,
  ) {
    this.activeLanguageSubscription =
      this.languageService.activeLanguageObject.subscribe(() => {
        if(this.authService.user.getValue()){
          this.getConsents(true).subscribe();
        }
    });

    this.missingConsentSlugs = this._missingConsentSlugs.asObservable();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let currentRoute = this.route.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        return currentRoute;
      })
    ).subscribe((currentRoute) => {
      this.currentRoute.set(currentRoute);
      this.checkCurrentRouteForConsent(currentRoute);
    });

  }

  checkCurrentRouteForConsent(currentRoute: ActivatedRoute){
    const requiredConsentSlugs = currentRoute.snapshot.data?.consent ?? [];
    if (!requiredConsentSlugs.length || !this.consents()) {
      this.consentGenerationAttemptKey = undefined;
      this._consentGenerationError.set(undefined);
      this._missingConsentSlugs.next([]);
      return;
    }

    const missingConsentSlugs = this.getMissingConsents(requiredConsentSlugs);
    const missingConsentRecordSlugs = this.getMissingConsentRecordSlugs(requiredConsentSlugs);

    if (!missingConsentRecordSlugs.length) {
      this._missingConsentSlugs.next(missingConsentSlugs);
      this.consentGenerationAttemptKey = undefined;
      this._consentGenerationError.set(undefined);
      return;
    }

    const generationAttemptKey = this.makeConsentGenerationAttemptKey(missingConsentRecordSlugs);
    if (this.consentGenerationAttemptKey === generationAttemptKey || this.loadingObject()['consentGeneration']) {
      return;
    }

    this.consentGenerationAttemptKey = generationAttemptKey;
    this._consentGenerationError.set(undefined);

    this.generateMissingConsents(missingConsentRecordSlugs).subscribe({
      next: (createdConsents) => {
        if (!createdConsents.length) {
          this._consentGenerationError.set('error.could_not_load');
          this._missingConsentSlugs.next(missingConsentSlugs);
          return;
        }

        this.mergeConsentsIntoStore(createdConsents);
        this._consentGenerationError.set(undefined);
        this.checkCurrentRouteForConsent(currentRoute);
      },
      error: () => {
        this._consentGenerationError.set('error.could_not_load');
        this._missingConsentSlugs.next(missingConsentSlugs);
      },
    });

    this._missingConsentSlugs.next(missingConsentSlugs);
  }

  isAnythingLoading(): boolean {
    return this.loadingService.isAnythingLoading(this._loadingObject());
  }

  setLoadingStatus(key: string, status: boolean): void {
    this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  private makeConsentGenerationAttemptKey(consentSlugs: string[]): string {
    return `${this.router.url}|${[...consentSlugs].sort().join(',')}`;
  }

  // Consent videos store duration in seconds (unlike other video-bearing content), so
  // convert to milliseconds here to match the millisecond convention used everywhere else.
  private normaliseConsentVideoDurations(consents: Consent[]): Consent[] {
    consents.forEach((consent) => {
      consent.videos?.forEach((video) => {
        if (video.duration !== null && video.duration !== undefined) {
          video.duration = video.duration * 1000;
        }
      });
    });
    return consents;
  }

  private getMissingConsentRecordSlugs(consentSlugs: string[]): string[] {
    return consentSlugs.filter((slug) => !this.getLatestConsent(slug));
  }

  private generateMissingConsents(consentSlugs: string[]): Observable<Consent[]> {
    const uniqueConsentSlugs = [...new Set(consentSlugs)].filter(Boolean);
    if (!uniqueConsentSlugs.length) {
      return of([]);
    }

    this.setLoadingStatus('consentGeneration', true);

    return this.http.post<GenerateConsentsResponse>('api/v1/consent/generate', { term_slugs: uniqueConsentSlugs }).pipe(
      map((response) => this.normaliseConsentVideoDurations(response?.created ?? [])),
      catchError((error) => {
        return this.handleError(error);
      }),
      finalize(() => {
        this.setLoadingStatus('consentGeneration', false);
      }),
    );
  }

  private mergeConsentsIntoStore(consents: Consent[]): void {
    if (!consents.length) {
      return;
    }

    const currentConsents = this.consents() ?? [];
    const mergedConsents = [...currentConsents];

    consents.forEach((consent) => {
      const existingConsentIndex = mergedConsents.findIndex((existingConsent) => existingConsent.id === consent.id);
      if (existingConsentIndex > -1) {
        mergedConsents[existingConsentIndex] = consent;
      } else {
        mergedConsents.push(consent);
      }
    });

    this._consents.set(mergedConsents);
  }

  clearTranslations() {
    this._consents.set(undefined);
    this._consentGenerationError.set(undefined);
    this.consentGenerationAttemptKey = undefined;
  };
  clearData() {
    this.clearTranslations();
  }
  getMissingConsents(consentSlugs: string[]): string[] {
    return consentSlugs.filter(slug => !this.hasConsentSingle(slug));
  }
  hasConsentSingle(slug: string): boolean {
      const latestConsent = this.getLatestConsent(slug);
      return !!(latestConsent && latestConsent.given_at && !latestConsent.withdrawn_at && !latestConsent.declined_at);
  }
  hasConsent(consentSlugs: string[]): boolean {
    return consentSlugs.every(slug => this.hasConsentSingle(slug));
  }
  getLatestConsent(slug: string): Consent | undefined {
    const allConsents = this.consents()?.filter(consent => consent.slug === slug);
    if (!allConsents?.length) {
      return undefined;
    } else {
      return this.sortByLatestActivity(allConsents)[0];
    }
  }

  getConsents (freshFromServer: boolean) : Observable<Consent[]> {

    if (this.consents() && !freshFromServer){
      return of(this.consents() ?? []);
    }

    return this.http.get<{data: Consent[]}>('api/v1/consent')
      .pipe(
        map(response =>{
          const firstLoad = !this.consents();
          this._consents.set(this.normaliseConsentVideoDurations(response.data));
          const currentRoute = this.currentRoute();
          if (firstLoad && currentRoute) {
            this.checkCurrentRouteForConsent(currentRoute);
          }
          return this.consents() ?? [];
        }),
        catchError((error) => {
          return this.handleError(error);
        })
      )
  }

  updateConsentStatus(consent: Consent, status: 'given' | 'declined' | 'withdrawn') : Observable<Consent[]> {
    const { id, term_id, term_version_id, user_id, target_type, target_id } = consent;
    const newConsent = {
      id,
      term_id,
      term_version_id,
      user_id,
      target_type,
      target_id,
      locale : this.languageService.activeLanguageObjectSynchronously?.languageKey,
      new_status: status
    };
    
    return this.http.put<{data: Consent[]}>(`api/v1/consent/${consent.id}/user`, newConsent)
      .pipe(
        map(response => {
          this._consents.set(this.normaliseConsentVideoDurations(response.data));
          const currentRoute = this.currentRoute();
          if (currentRoute) {
            this.checkCurrentRouteForConsent(currentRoute);
          }
          return this.consents() ?? [];
        }),
        catchError((error) => {
          return this.handleError(error);
        })
      );
  }

  sortByLatestActivity(consents: Consent[] = []): Consent[] {
    const sortedConsents = [...consents];
    sortedConsents.sort((a, b) => {
      // Extract the relevant timestamps, defaulting to a very early date if not present
      const activityTimeStampAttributes = this.activityTimeStampAttributes() as Array<keyof Consent>;
      const aMaxDate = new Date(Math.max(...activityTimeStampAttributes.map(prop => a[prop] ? new Date(a[prop]).getTime() : 0), 0));
      const bMaxDate = new Date(Math.max(...activityTimeStampAttributes.map(prop => b[prop] ? new Date(b[prop]).getTime() : 0), 0));
    
      // Compare the dates to sort in descending order; if the dates are equal, sort by ID
      const dateComparison = Number(bMaxDate) - Number(aMaxDate);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return b.id - a.id;
    });
    return sortedConsents;
  }

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    } else {
      const message = errorResponse.error.message;
      const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(message);
      if(standardErrorMessageTranslationKey){
        errorMessage = standardErrorMessageTranslationKey;
      } else if(message.includes('Request latest data then try again.')){
        errorMessage = 'error.refresh';
      }
      if (errorResponse.error.meta){
        return throwError({message:errorMessage,meta:errorResponse.error.meta});
      }
      return throwError(errorMessage);
    }
  }

}
